
export default {
    name: 'PinInput',
    
    props: {
        formKey: {
            type: [String, Number],
            required: true
        },
        email: {
            type: String,
            required: true
        },
        error: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            pinDigits: Array(5).fill(''),
            isResending: false,
            resendSuccess: false
        };
    },

    mounted() {
        // Focus first input on mount
        this.$nextTick(() => {
            const firstInput = this.$refs['pinInput0'];
            if (firstInput && firstInput[0]) {
                firstInput[0].focus();
            }
        });
    },

    methods: {
        handlePaste(event) {
            event.preventDefault();
            const pastedText = (event.clipboardData || window.clipboardData)
                .getData('text')
                .replace(/\s/g, '')
                .toUpperCase()
                .slice(0, 5);

            if (!/^[A-Z0-9]+$/.test(pastedText)) {
                return;
            }

            const pastedChars = pastedText.split('');
            this.pinDigits = Array(5).fill('');
            pastedChars.forEach((char, index) => {
                if (index < 5) {
                    this.pinDigits[index] = char;
                }
            });

            const nextEmptyIndex = this.pinDigits.findIndex(digit => !digit);
            const targetIndex = nextEmptyIndex === -1 ? 4 : nextEmptyIndex;
            
            this.$nextTick(() => {
                const targetInput = this.$refs[`pinInput${targetIndex}`];
                if (targetInput && targetInput[0]) {
                    targetInput[0].focus();
                }

                if (this.pinDigits.every(digit => digit)) {
                    this.emitPin();
                }
            });
        },

        handleKeydown(event, index) {
            const currentInput = this.$refs[`pinInput${index}`][0];
            
            switch (event.key) {
                case 'Backspace':
                    if (!this.pinDigits[index] && index > 0) {
                        event.preventDefault();
                        this.pinDigits[index - 1] = '';
                        const prevInput = this.$refs[`pinInput${index - 1}`][0];
                        prevInput && prevInput.focus();
                    }
                    break;

                case 'ArrowLeft':
                    if (index > 0) {
                        event.preventDefault();
                        const prevInput = this.$refs[`pinInput${index - 1}`][0];
                        prevInput && prevInput.focus();
                    }
                    break;

                case 'ArrowRight':
                    if (index < 4) {
                        event.preventDefault();
                        const nextInput = this.$refs[`pinInput${index + 1}`][0];
                        nextInput && nextInput.focus();
                    }
                    break;
            }
        },

        focusNext(index) {
            this.pinDigits[index] = this.pinDigits[index].toUpperCase();

            const currentInput = this.pinDigits[index];
            if (!/^[A-Z0-9]$/.test(currentInput)) {
                this.pinDigits[index] = '';
                return;
            }

            if (currentInput && currentInput.length === 1 && index < 4) {
                const nextInput = this.$refs[`pinInput${index + 1}`];
                if (nextInput && nextInput[0]) {
                    nextInput[0].focus();
                }
            }

            if (this.pinDigits.every(digit => digit.length === 1)) {
                this.emitPin();
            }
        },

        validateKeyPress(event) {
            const charCode = event.which || event.keyCode;
            const char = String.fromCharCode(charCode);
            if (!/^[a-zA-Z0-9]$/.test(char)) {
                event.preventDefault();
            }
        },

        emitPin() {
            const pin = this.pinDigits.join('');
            this.$emit('pin-entered', pin);
        },

        async handleResendPin() {
            if (!this.email || !this.formKey) {
                this.$emit('error', 'Email and form key are required to resend PIN.');
                return;
            }

            try {
                this.isResending = true;
                const response = await this.$member.auth.sendPinToEmail(this.email, this.formKey);
                
                if (response.status === 200) {
                    // Emit the new form key if provided
                    if (response.data?.member?.sent_pin?.key) {
                        this.$emit('update:formKey', response.data.member.sent_pin.key);
                    }

                    this.resendSuccess = true;
                    this.$emit('error', ''); // Clear any existing errors
                    
                    // Reset success message after 3 seconds
                    setTimeout(() => {
                        this.resendSuccess = false;
                    }, 3000);
                    
                    // Clear and reset focus
                    this.pinDigits = Array(5).fill('');
                    this.$nextTick(() => {
                        const firstInput = this.$refs['pinInput0'];
                        if (firstInput && firstInput[0]) {
                            firstInput[0].focus();
                        }
                    });
                }
            } catch (error) {
                console.error('Failed to resend PIN:', error);
                const errorMessage = error.response?.data?.message || 'Failed to resend PIN. Please try again later.';
                this.$emit('error', errorMessage);
                
                if (error.response?.status === 403 || error.response?.status === 401) {
                    this.$emit('invalid-key');
                }
            } finally {
                this.isResending = false;
            }
        },

        reset() {
            this.pinDigits = Array(5).fill('');
            this.isResending = false;
            this.resendSuccess = false;
            
            this.$nextTick(() => {
                const firstInput = this.$refs['pinInput0'];
                if (firstInput && firstInput[0]) {
                    firstInput[0].focus();
                }
            });
        }
    }
};
